import React, { useEffect, useMemo, useState } from 'react';
import {
  Checkbox, createStyles,
  ExpansionPanel, ExpansionPanelDetails, ExpansionPanelSummary, Grid, Link, Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useTranslation } from 'react-i18next';
import { getOptionLabel } from '@michelin/select-options-provider';
import { Theme } from '@material-ui/core/styles';
import { makeStyles } from '@material-ui/styles';
import uuid from 'uuid';
import {
  Contact, ContactListItem, ContactListItemAction, SPPContactTypeLevelsEnum,
} from '../../../Types';
import usePermissionsSPP from '../../../hooks/usePermissionsSPP';
import ContactsListTable from '../ListTable';
import { getNewContact, RetrieveRoleAreaPriorityLevel } from '../Utils';
import { ContactDetailModal } from '../DetailModal';

const useStyles = makeStyles((theme: Theme) => createStyles({
  heading: {
    fontSize: theme.typography.pxToRem(18),
    lineHeight: theme.typography.pxToRem(18),
    flexBasis: '80%',
    flexShrink: 0,
    fontWeight: 'bold',
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    lineHeight: theme.typography.pxToRem(18),
    flexBasis: '20%',
    color: theme.palette.text.primary,
  },
}));

interface PriorityLevelsReassignmentProps {
  stNumber: string,
  btNumber: string,
  countryCode: string,
  reassignedContact: Contact,
  reassignmentsMap: Map<string, string>,
  onChange: (reassignedMap: Map<string, ContactListItem | Contact>) => void,
}

function PriorityLevelsReassignment(props:PriorityLevelsReassignmentProps) {
  const {
    stNumber,
    btNumber,
    countryCode,
    reassignedContact,
    reassignmentsMap,
    onChange,
  } = props;

  const { t } = useTranslation();
  const permissions = usePermissionsSPP();
  const createAllowed = permissions.allowsAction('contacts.create');
  const classes = useStyles();

  const [reassignments, setReassignments] = useState<any[]>([]);
  const [assignmentsMap] = useState<Map<string, Contact | ContactListItem>>(new Map<string, Contact | ContactListItem>());
  const [assignmentsKey, setAssignmentsKey] = useState<string>('');
  const [addingContact, setAddingContact] = useState<string | null>(null);

  useEffect(() => {
    const arr:any[] = [];
    if (reassignmentsMap.size) {
      reassignmentsMap.forEach((value: string, key:string) => {
        arr.push(
          {
            service: key.split('~').shift(),
            role_area: key.split('~').pop(),
            level: value,
          },
        );
      });
    }
    setReassignments(arr);
  }, [reassignmentsMap]);

  useEffect(() => {
    onChange(assignmentsMap);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentsKey]);

  function assignContact(id:string, contact: Contact | ContactListItem) {
    const level = reassignmentsMap.get(id);
    assignmentsMap.set(`${id}~${level}`, contact);
    setAssignmentsKey(JSON.stringify(Array.from(assignmentsMap.entries())));
  }

  function undoAssignContact(id:string) {
    const level = reassignmentsMap.get(id);
    assignmentsMap.delete(`${id}~${level}`);
    setAssignmentsKey(JSON.stringify(Array.from(assignmentsMap.entries())));
  }

  function reassignedContactInfo(key: string, contact: Contact | ContactListItem):JSX.Element {
    return (
      <Grid>
        <b>Reassigned to:</b>
        {' '}
        {contact.first_name}
        {' '}
        {contact.last_name}
        {' '}
        [
        <Link style={{ cursor: 'pointer' }} onClick={() => undoAssignContact(key)}>Remove</Link>
        ]
        <br />
        {contact.work_phone ? (
          <div>
            <b>Work Phone:</b>
            {' '}
            {contact.work_phone}
            {' '}
          </div>
        ) : null}
        {contact.cell_phone ? (
          <div>
            <b>Cell Phone:</b>
            {' '}
            {contact.cell_phone}
            {' '}
          </div>
        ) : null}
        {contact.email_address ? (
          <div>
            <b>Email:</b>
            {' '}
            {contact.email_address}
            {' '}
          </div>
        ) : null}
      </Grid>
    );
  }

  function createRenderComponent(customAction: ContactListItemAction, contact: ContactListItem): JSX.Element {
    const { additionalInfo } = customAction;
    const currentContactPriorityLevel: string | null = RetrieveRoleAreaPriorityLevel(contact, additionalInfo.service, additionalInfo.roleArea);
    const disableAssign: boolean = (customAction.additionalInfo.contact.id === contact.id)
      || (currentContactPriorityLevel !== SPPContactTypeLevelsEnum.general && currentContactPriorityLevel !== null);

    let checked = additionalInfo.contact.id === contact.id;
    const id = `${additionalInfo.service}~${additionalInfo.role_area}`;
    return (
      <div style={{ textAlign: 'center' }}>
        <span style={{ margin: '0.5rem', fontSize: '13px' }}>{customAction.label}</span>
        <br />
        <Checkbox
          checked={checked}
          disabled={disableAssign}
          disableFocusRipple
          disableRipple
          disableTouchRipple
          style={{ padding: 0, margin: '0.5rem' }}
          onChange={() => {
            checked = true;
            assignContact(id, contact);
          }}
        />
      </div>
    );
  }

  function getCustomActions(service:string, role_area:string, contact:Contact | ContactListItem) {
    const actionList:ContactListItemAction[] = [];
    actionList.push({
      id: `${service}~${role_area}`,
      label: t('Assign'),
      action: assignContact,
      additionalInfo: {
        service,
        role_area,
        contact,
      },
      renderElement: createRenderComponent,
    });
    return actionList;
  }

  function renderAssignContact({ service, role_area }:{ service:string, role_area:string }):JSX.Element {
    const contactLevel:'All'|'BT'|'ST' = (reassignedContact.contact_level === 'BT' || reassignedContact.contact_level === 'ST')
      ? reassignedContact.contact_level
      : 'All';

    const contactTypes = [{
      service,
      role_areas: [
        {
          role_area,
          levels: [
            {
              level: SPPContactTypeLevelsEnum.general,
              location: `10~${reassignedContact.contact_level === 'ST' ? stNumber : btNumber}`,
            },
          ],
        },
      ],
      id: uuid.v4(),
    }];

    const data:Contact = getNewContact({ contact_types: contactTypes });
    const serviceName:string = getOptionLabel('dealer_dealer_contact_types_services', service);
    const roleAreaName:string = getOptionLabel('dealer_dealer_contact_types_role_areas', role_area);

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} style={{ marginTop: 7 }}>
          {!addingContact
            ? (
              <ContactsListTable
                stNumber={stNumber}
                btNumber={btNumber}
                contactLevel={contactLevel}
                customActions={getCustomActions(service, role_area, reassignedContact)}
              />
            )
            : (
              <ContactDetailModal
                onClose={() => { setAddingContact(null); }}
                onSave={(contact: Contact) => { assignContact(`${service}~${role_area}`, contact); setAddingContact(null); }}
                defaultData={data}
                countryCode={countryCode}
                title={`${serviceName} - ${roleAreaName}`}
              />
            )}
        </Grid>
      </Grid>
    );
  }

  return (
    useMemo(() => (
      <>
        {reassignments.map((elem:any) => {
          const key = `${elem.service}~${elem.role_area}`;
          const service = getOptionLabel('dealer_dealer_contact_types_services', elem.service);
          const role_area = getOptionLabel('dealer_dealer_contact_types_role_areas', elem.role_area);
          const level = getOptionLabel('dealer_dealer_contact_types_levels', elem.level);
          const contact = assignmentsMap.get(`${key}~${elem.level}`);
          return (
            <div key={key} style={{ marginBottom: '0.3rem' }}>
              <ExpansionPanel TransitionProps={{ unmountOnExit: true }} disabled={false} defaultExpanded>
                <ExpansionPanelSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-label={t('Expand')}
                  id={key}
                >
                  <Typography className={classes.heading}>{`${service} - ${role_area} [${level}]`}</Typography>
                  {createAllowed
                    ? (
                      <Typography
                        className={classes.secondaryHeading}
                        onClick={(event) => {
                          event.stopPropagation();
                          setAddingContact(key);
                        }}
                      >
                        <Link><b>{t('Add a new contact')}</b></Link>
                      </Typography>
                    )
                    : null}
                </ExpansionPanelSummary>
                <ExpansionPanelDetails style={{ padding: (contact ? 24 : 0) }}>
                  {contact
                    ? reassignedContactInfo(key, contact)
                    : renderAssignContact({ service: elem.service, role_area: elem.role_area })}
                </ExpansionPanelDetails>
              </ExpansionPanel>
            </div>
          );
        })}
      </>
      // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [reassignments, assignmentsKey, addingContact])
  );
}

export {
  PriorityLevelsReassignment,
};
